import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { wizardApi } from '@/lib/api';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { MultiFileUpload } from '@/components/wizard/MultiFileUpload';
import { ExtractionReview } from '@/components/wizard/ExtractionReview';
import { DealTypeSelector } from '@/components/wizard/DealTypeSelector';
import { ProposalPreferenceStep } from '@/components/wizard/ProposalPreferenceStep';
import { TemplateVariablesStep } from '@/components/wizard/TemplateVariablesStep';
import { QuestionsForm } from '@/components/wizard/QuestionsForm';
import { ConfirmationStep } from '@/components/wizard/ConfirmationStep';
import EvaluatingStep from '@/components/wizard/EvaluatingStep';
import QualifiedStep from '@/components/wizard/QualifiedStep';
import ClarifyingStep from '@/components/wizard/ClarifyingStep';
import AcknowledgedStep from '@/components/wizard/AcknowledgedStep';
import { toast } from '@solid/ui';
import type {
  PublicSubmission,
  SubmissionDocument,
  WizardQuestion,
  QualificationResult,
  DealType,
  AiSdrResult,
  DynamicQuestion,
} from '@/types';

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type WizardState =
  | 'LOADING'
  | 'NOT_FOUND'
  | 'UPLOAD'
  | 'ANALYZING'
  | 'REVIEW'
  | 'DEAL_TYPE'
  | 'PROPOSAL_PREF'
  | 'QUESTIONS'
  | 'TEMPLATE_VARS'
  | 'EVALUATING'
  | 'QUALIFIED'
  | 'CLARIFYING'
  | 'ACKNOWLEDGED'
  | 'SUBMITTING'
  | 'COMPLETE'
  | 'ERROR';

type ProposalPreference = 'COMBINED' | 'SEPARATE';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface StepContext {
  hasDocument: boolean;
  hasMultipleTypes: boolean;
  hasTemplateVars: boolean;
}

/** Calculate total steps based on path and optional steps */
function totalSteps(ctx: StepContext): number {
  // Base steps:
  // - Path A: Upload + Review + Deal Type + Questions + Confirm = 5
  // - Path B: Deal Type + Questions + Confirm = 3
  let steps = ctx.hasDocument ? 5 : 3;
  if (ctx.hasMultipleTypes) steps++; // Proposal preference step
  if (ctx.hasTemplateVars) steps++; // Template variables step
  return steps;
}

/** Map the internal wizard state to a 1-based step index */
function stepFromState(state: WizardState, ctx: StepContext): number {
  const { hasDocument, hasMultipleTypes } = ctx;

  if (hasDocument) {
    // Path A: Upload → Review → Deal Type → [Proposal Pref] → Questions → [Template Vars] → Confirm
    switch (state) {
      case 'UPLOAD':
      case 'ANALYZING':
        return 1;
      case 'REVIEW':
        return 2;
      case 'DEAL_TYPE':
        return 3;
      case 'PROPOSAL_PREF':
        return 4;
      case 'QUESTIONS':
        return hasMultipleTypes ? 5 : 4;
      case 'TEMPLATE_VARS':
        return hasMultipleTypes ? 6 : 5;
      case 'SUBMITTING':
      case 'COMPLETE':
        return totalSteps(ctx);
      default:
        return 1;
    }
  }

  // Path B: Deal Type → [Proposal Pref] → Questions → [Template Vars] → Confirm
  switch (state) {
    case 'DEAL_TYPE':
      return 1;
    case 'PROPOSAL_PREF':
      return 2;
    case 'QUESTIONS':
      return hasMultipleTypes ? 3 : 2;
    case 'TEMPLATE_VARS':
      return hasMultipleTypes ? 4 : 3;
    case 'SUBMITTING':
    case 'COMPLETE':
      return totalSteps(ctx);
    default:
      return 1;
  }
}

function stepTitle(state: WizardState): string {
  switch (state) {
    case 'UPLOAD':
      return 'Upload Your Document';
    case 'ANALYZING':
      return 'Analyzing Your Document';
    case 'REVIEW':
      return 'What We Found';
    case 'DEAL_TYPE':
      return 'Select Project Type(s)';
    case 'PROPOSAL_PREF':
      return 'Proposal Preference';
    case 'QUESTIONS':
      return 'Qualifying Questions';
    case 'TEMPLATE_VARS':
      return 'Additional Details';
    case 'EVALUATING':
      return 'Reviewing Your Project';
    case 'QUALIFIED':
      return "Let's Talk!";
    case 'CLARIFYING':
      return 'A Few Quick Questions';
    case 'ACKNOWLEDGED':
      return 'Thank You!';
    case 'SUBMITTING':
      return 'Submitting...';
    case 'COMPLETE':
      return 'Submission Complete';
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SubmitWizard() {
  const { id } = useParams<{ id: string }>();

  // Core state
  const [wizardState, setWizardState] = useState<WizardState>('LOADING');
  const [submission, setSubmission] = useState<PublicSubmission | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Path A (document) state
  // Documents state (all docs in SubmissionDocument table)
  const [additionalDocs, setAdditionalDocs] = useState<SubmissionDocument[]>([]);

  // Deal type selection state (shared, but different entry point)
  const [dealTypes, setDealTypes] = useState<DealType[]>([]);
  const [selectedDealTypeIds, setSelectedDealTypeIds] = useState<string[]>([]);
  const [suggestedDealTypeIds, setSuggestedDealTypeIds] = useState<string[]>([]);
  const [dealTypesLoading, setDealTypesLoading] = useState(false);

  // Proposal preference state (only when 2+ types selected)
  const [proposalPreference, setProposalPreference] = useState<ProposalPreference | null>(null);

  // Template variables state
  const [templateVariables, setTemplateVariables] = useState<string[]>([]);
  const [templateValues, setTemplateValues] = useState<Record<string, string>>({});

  // Shared question state
  const [questions, setQuestions] = useState<WizardQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(0);

  // Result state
  const [result, setResult] = useState<QualificationResult | null>(null);

  // AI SDR evaluation state
  const [aiSdrResult, setAiSdrResult] = useState<AiSdrResult | null>(null);
  const [clarifyingQuestions, setClarifyingQuestions] = useState<DynamicQuestion[]>([]);
  const [bookingUrl, setBookingUrl] = useState<string>('');

  // ---------------------------------------------------------------------------
  // Initial load — fetch submission
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!id) {
      setWizardState('NOT_FOUND');
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const data: PublicSubmission = await wizardApi.getSubmission(id!);
        if (cancelled) return;

        setSubmission(data);

        // If the submission is already complete, jump straight to confirmation
        if (data.wizardStatus === 'COMPLETE' || data.wizardStatus === 'SUBMITTED') {
          // Derive a result from what we have
          const score = data.aiReviewResult?.qualificationScore ?? 0;
          setResult({
            qualified: score >= 50,
            qualificationScore: score,
            dealId: null,
            bookingUrl: '',
          });
          setWizardState('COMPLETE');
          return;
        }

        // Restore documents if present
        if (data.documents?.length) {
          setAdditionalDocs(data.documents);
        }

        // Restore selected deal types if present
        if (data.selectedDealTypeIds?.length) {
          setSelectedDealTypeIds(data.selectedDealTypeIds);
        }
        if (data.proposalPreference) {
          setProposalPreference(data.proposalPreference);
        }

        // Resume from the furthest completed step
        if (data.hasDocument) {
          if (data.selectedDealTypeIds?.length) {
            // Already past deal type selection — go to questions
            setWizardState('DEAL_TYPE');
          } else if (data.aiReviewResult) {
            // AI analysis done — go to review
            const suggestedIds = data.aiReviewResult.suggestedDealTypeIds || [];
            setSuggestedDealTypeIds(suggestedIds);
            if (suggestedIds.length && !data.selectedDealTypeIds?.length) {
              setSelectedDealTypeIds(suggestedIds);
            }
            setWizardState('REVIEW');
          } else if (data.documents?.length) {
            // Docs uploaded but not analyzed
            setWizardState('UPLOAD');
          } else {
            setWizardState('UPLOAD');
          }
        } else {
          setWizardState('DEAL_TYPE');
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setWizardState('NOT_FOUND');
        } else {
          setError('Failed to load submission. Please try again.');
          setWizardState('ERROR');
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // ---------------------------------------------------------------------------
  // Auto-run evaluation when entering EVALUATING state
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (wizardState !== 'EVALUATING' || !id) return;

    let cancelled = false;

    async function runEvaluation() {
      try {
        // Submit any answers we have first
        const answersArray = Object.entries(answers)
          .filter(([, value]) => value.trim() !== '')
          .map(([questionId, answer]) => ({
            questionId,
            answer,
          }));

        if (answersArray.length > 0) {
          await wizardApi.submitAnswers(id!, answersArray);
        }

        // Trigger AI SDR evaluation
        const evalResult = await wizardApi.evaluate(id!);
        if (cancelled) return;

        setAiSdrResult(evalResult);
        setBookingUrl(evalResult.bookingUrl || '');

        // Route based on decision - no CLARIFYING step, just QUALIFIED or ACKNOWLEDGED
        if (evalResult.decision === 'QUALIFIED') {
          setWizardState('QUALIFIED');
        } else {
          // UNCERTAIN or NOT_QUALIFIED both go to acknowledged
          setWizardState('ACKNOWLEDGED');
        }
      } catch (err: any) {
        console.error('Evaluation error:', err);
        if (!cancelled) {
          setWizardState('ACKNOWLEDGED');
        }
      }
    }

    runEvaluation();

    return () => {
      cancelled = true;
    };
  }, [wizardState, id, answers]);

  // Path Pilot: track "Thanks for Reaching Out" page view
  useEffect(() => {
    if (wizardState !== 'ACKNOWLEDGED') return;

    fetch('/ppilot/info', {                                                                                                                                                                                                   
      method: 'POST',                  
      headers: { 'Content-Type': 'application/json' },                                                                                                                                                                        
      body: JSON.stringify({                                                                                                                                                                                                  
        trackingKey: '98092f9e-51e3-4206-ae82-5a0ba59b8205',                                                                                                                                                                  
        eventType: 'pageview',                                                                                                                                                                                                
        pageUrl: 'https://sales.soliddigital.com/thank-you',
        referrer: document.referrer || undefined,                                                                                                                                                                                 
        screenWidth: window.screen.width,                 
      }),                                                                                                                                                                                                                     
      keepalive: true,
    });                      
  }, [wizardState]);

  // ---------------------------------------------------------------------------
  // Load deal types when entering REVIEW state (for project type selector)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (wizardState !== 'REVIEW' || dealTypes.length > 0) return;

    let cancelled = false;

    async function loadTypes() {
      try {
        setDealTypesLoading(true);
        const types = await wizardApi.getDealTypes();
        if (!cancelled) {
          setDealTypes(types);
          // Pre-select AI-suggested type
          const suggestedIds = submission?.aiReviewResult?.suggestedDealTypeIds || [];
          setSuggestedDealTypeIds(suggestedIds);
          setSelectedDealTypeIds(suggestedIds);
          setDealTypesLoading(false);
        }
      } catch {
        if (!cancelled) setDealTypesLoading(false);
      }
    }

    loadTypes();

    return () => {
      cancelled = true;
    };
  }, [wizardState, dealTypes.length, submission?.aiReviewResult?.suggestedDealTypeIds]);

  // ---------------------------------------------------------------------------
  // Path A: Upload handlers
  // ---------------------------------------------------------------------------

  const handleContinueToAnalysis = useCallback(async () => {
    if (!id) return;

    try {
      fetch('/ppilot/info', {                                                                                                                                                                                                   
        method: 'POST',                  
        headers: { 'Content-Type': 'application/json' },                                                                                                                                                                        
        body: JSON.stringify({                                                                                                                                                                                                  
          trackingKey: '98092f9e-51e3-4206-ae82-5a0ba59b8205',                                                                                                                                                                  
          eventType: 'pageview',                                                                                                                                                                                                
          pageUrl: 'https://sales.soliddigital.com/rfp-analysis',
          referrer: document.referrer || undefined,                                                                                                                                                                             
          screenWidth: window.screen.width,                 
        }),                                                                                                                                                                                                                     
        keepalive: true,
      });                      

      setWizardState('ANALYZING');

      const analysisResult = await wizardApi.analyzeDocument(id);

      setSubmission((prev) =>
        prev ? { ...prev, aiReviewResult: analysisResult } : prev
      );

      setWizardState('REVIEW');
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Analysis failed. Please try again.';
      toast.error(message);
      setWizardState('UPLOAD');
    }
  }, [id]);

  // ---------------------------------------------------------------------------
  // Path A: Review → Deal Type transition
  // ---------------------------------------------------------------------------

  const handleReviewContinue = useCallback(async (correctedMetadata?: Record<string, any>) => {
    if (!id) return;

    try {
      fetch('/ppilot/info', {                                                                                                                                                                                                   
        method: 'POST',                  
        headers: { 'Content-Type': 'application/json' },                                                                                                                                                                        
        body: JSON.stringify({                                                                                                                                                                                                  
          trackingKey: '98092f9e-51e3-4206-ae82-5a0ba59b8205',                                                                                                                                                                  
          eventType: 'pageview',                                                                                                                                                                                                
          pageUrl: 'https://sales.soliddigital.com/rfp-review',
          referrer: document.referrer || undefined,                                                                                                                                                                                   
          screenWidth: window.screen.width,                 
        }),                                                                                                                                                                                                                     
        keepalive: true,
      });                      

      // Save corrected metadata if provided
      if (correctedMetadata) {
        await wizardApi.updateMetadata(id, correctedMetadata);
        // Also update local submission state
        setSubmission((prev) =>
          prev?.aiReviewResult
            ? {
                ...prev,
                aiReviewResult: {
                  ...prev.aiReviewResult,
                  extractedMetadata: correctedMetadata,
                },
              }
            : prev
        );
      }

      // Load deal types if not already loaded
      if (dealTypes.length === 0) {
        setDealTypesLoading(true);
        const types = await wizardApi.getDealTypes();
        setDealTypes(types);
        setDealTypesLoading(false);
      }

      // Pre-select AI-suggested types
      const suggestedIds = submission?.aiReviewResult?.suggestedDealTypeIds || [];
      setSuggestedDealTypeIds(suggestedIds);
      setSelectedDealTypeIds(suggestedIds);

      // Go to deal type selection (with AI suggestions pre-selected)
      setWizardState('DEAL_TYPE');
    } catch (err: any) {
      setDealTypesLoading(false);
      const message =
        err?.response?.data?.message || 'Failed to load project types.';
      toast.error(message);
    }
  }, [id, submission?.aiReviewResult?.suggestedDealTypeIds, dealTypes.length]);

  // ---------------------------------------------------------------------------
  // Deal type handlers (used by both paths)
  // ---------------------------------------------------------------------------

  // Load deal types when entering DEAL_TYPE state (Path B only - Path A loads in handleReviewContinue)
  useEffect(() => {
    if (wizardState !== 'DEAL_TYPE' || dealTypes.length > 0) return;

    let cancelled = false;

    async function loadDealTypes() {
      setDealTypesLoading(true);
      try {
        const types = await wizardApi.getDealTypes();
        if (!cancelled) {
          setDealTypes(types);
          setDealTypesLoading(false);
        }
      } catch {
        if (!cancelled) {
          setDealTypesLoading(false);
          toast.error('Failed to load project types.');
        }
      }
    }

    loadDealTypes();

    return () => {
      cancelled = true;
    };
  }, [wizardState, dealTypes.length]);

  const handleDealTypeToggle = useCallback((dealTypeId: string) => {
    setSelectedDealTypeIds((prev) =>
      prev.includes(dealTypeId)
        ? prev.filter((id) => id !== dealTypeId)
        : [...prev, dealTypeId]
    );
  }, []);

  const handleDealTypeContinue = useCallback(async () => {
    if (!id || selectedDealTypeIds.length === 0) return;

    try {
      // Save selected types to backend (always combined for simplicity)
      await wizardApi.selectDealTypes(id, selectedDealTypeIds, 'COMBINED');

      fetch('/ppilot/info', {                                                                                                                                                                                                   
        method: 'POST',                  
        headers: { 'Content-Type': 'application/json' },                                                                                                                                                                        
        body: JSON.stringify({                                                                                                                                                                                                  
          trackingKey: '98092f9e-51e3-4206-ae82-5a0ba59b8205',                                                                                                                                                                  
          eventType: 'pageview',                                                                                                                                                                                                
          pageUrl: 'https://sales.soliddigital.com/deal-type-selection',
          referrer: document.referrer || undefined,                                                                                                                                                                            
          screenWidth: window.screen.width,                 
        }),                                                                                                                                                                                                                     
        keepalive: true,
      });                      

      // Skip qualifying questions — go straight to evaluation.
      // The AI SDR has enough signal from the document analysis,
      // deal type selection, and LeadMagic enrichment.
      setWizardState('EVALUATING');
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Failed to save project types.';
      toast.error(message);
    }
  }, [id, selectedDealTypeIds]);

  // ---------------------------------------------------------------------------
  // Proposal preference handlers
  // ---------------------------------------------------------------------------

  const handleProposalPreferenceSelect = useCallback(async (pref: ProposalPreference) => {
    if (!id) return;

    setProposalPreference(pref);

    try {
      // Update preference on backend
      await wizardApi.selectDealTypes(id, selectedDealTypeIds, pref);

      // Move to questions
      await loadQuestionsAndContinue();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Failed to save preference.';
      toast.error(message);
    }
  }, [id, selectedDealTypeIds]);

  // ---------------------------------------------------------------------------
  // Shared: Question / Answer handlers
  // ---------------------------------------------------------------------------

  const handleAnswerChange = useCallback(
    (questionId: string, answer: string) => {
      setAnswers((prev) => ({ ...prev, [questionId]: answer }));
    },
    []
  );

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  // ---------------------------------------------------------------------------
  // AI SDR Evaluation
  // ---------------------------------------------------------------------------

  const triggerEvaluation = useCallback(async () => {
    if (!id) return;

    setWizardState('EVALUATING');

    try {
      // First, submit any answers we have
      const answersArray = Object.entries(answers)
        .filter(([, value]) => value.trim() !== '')
        .map(([questionId, answer]) => ({
          questionId,
          answer,
        }));

      if (answersArray.length > 0) {
        await wizardApi.submitAnswers(id, answersArray);
      }

      // Now trigger AI SDR evaluation
      const evalResult = await wizardApi.evaluate(id);
      setAiSdrResult(evalResult);
      setBookingUrl(evalResult.bookingUrl || '');

      // Route based on decision
      if (evalResult.decision === 'QUALIFIED') {
        setWizardState('QUALIFIED');
      } else if (evalResult.decision === 'UNCERTAIN' && evalResult.dynamicQuestions?.length) {
        setClarifyingQuestions(evalResult.dynamicQuestions);
        setWizardState('CLARIFYING');
      } else {
        // NOT_QUALIFIED or UNCERTAIN with no questions
        setWizardState('ACKNOWLEDGED');
      }
    } catch (err: any) {
      console.error('Evaluation error:', err);
      // If evaluation fails, fall back to acknowledged state
      setWizardState('ACKNOWLEDGED');
    }
  }, [id, answers]);

  // ---------------------------------------------------------------------------
  // Clarifying questions handlers
  // ---------------------------------------------------------------------------

  const handleClarifyingSubmit = useCallback(async (clarifyingAnswers: Record<string, string>) => {
    if (!id) return;

    setWizardState('EVALUATING');

    try {
      // Submit clarifying answers and re-evaluate
      const evalResult = await wizardApi.submitClarifyingAnswers(id, clarifyingAnswers);
      setAiSdrResult(evalResult);
      setBookingUrl(evalResult.bookingUrl || '');

      // Route based on new decision
      if (evalResult.decision === 'QUALIFIED') {
        setWizardState('QUALIFIED');
      } else if (evalResult.decision === 'UNCERTAIN' && evalResult.dynamicQuestions?.length) {
        // If still uncertain with more questions (shouldn't happen often)
        setClarifyingQuestions(evalResult.dynamicQuestions);
        setWizardState('CLARIFYING');
      } else {
        // NOT_QUALIFIED or done with questions
        setWizardState('ACKNOWLEDGED');
      }
    } catch (err: any) {
      console.error('Clarify error:', err);
      setWizardState('ACKNOWLEDGED');
    }
  }, [id]);

  // ---------------------------------------------------------------------------
  // Handle booking call
  // ---------------------------------------------------------------------------

  const handleBookCall = useCallback(() => {
    // Booking URL opens in new tab via QualifiedStep
    // Mark submission as QUALIFIED in backend
    if (id) {
      wizardApi.submitAnswers(id, []).catch(() => {
        // Silent fail - booking link already opened
      });
    }
  }, [id]);

  // ---------------------------------------------------------------------------
  // Final submission (for legacy flow, now triggers evaluation)
  // ---------------------------------------------------------------------------

  const handleFinalSubmit = useCallback(async () => {
    await triggerEvaluation();
  }, [triggerEvaluation]);

  // ---------------------------------------------------------------------------
  // Check for template variables and continue (shared by questions skip and complete)
  // ---------------------------------------------------------------------------

  const checkTemplateVarsAndContinue = useCallback(async () => {
    if (!id) return;

    try {
      // Check if any selected deal types have template variables
      const allVariables: string[] = [];
      for (const typeId of selectedDealTypeIds) {
        try {
          const varInfo = await wizardApi.getTemplateVariables(typeId);
          if (varInfo.variables && varInfo.variables.length > 0) {
            allVariables.push(...varInfo.variables);
          }
        } catch {
          // Type may not have a template, skip
        }
      }

      // Deduplicate variables
      const uniqueVariables = [...new Set(allVariables)];

      if (uniqueVariables.length > 0) {
        setTemplateVariables(uniqueVariables);

        // Pre-fill from extracted metadata if available
        const extractedMetadata = submission?.aiReviewResult?.extractedMetadata || {};
        setTemplateValues(extractedMetadata);

        setWizardState('TEMPLATE_VARS');
      } else {
        // No template variables, submit directly
        await handleFinalSubmit();
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Failed to check templates.';
      toast.error(message);
    }
  }, [id, selectedDealTypeIds, submission?.aiReviewResult?.extractedMetadata, handleFinalSubmit]);

  // ---------------------------------------------------------------------------
  // Load questions and optionally skip to template variables
  // ---------------------------------------------------------------------------

  const loadQuestionsAndContinue = useCallback(async () => {
    if (!id) return;

    try {
      // Load questions for selected types
      const questionsResponse = await wizardApi.getQuestionsForTypes(id, selectedDealTypeIds);

      // Pre-fill answers from any already-answered questions (from AI analysis)
      const prefilled: Record<string, string> = {};
      for (const q of questionsResponse.questions || []) {
        if (q.answer) {
          prefilled[q.questionId] = q.answer;
        }
      }
      setAnswers(prefilled);

      // Filter to only unanswered questions
      const unanswered = (questionsResponse.questions || []).filter(
        (q: WizardQuestion) => !q.answer
      );
      setQuestions(unanswered);
      setCurrentPage(0);

      // If no questions to answer, skip to template vars or submission
      if (unanswered.length === 0) {
        await checkTemplateVarsAndContinue();
      } else {
        setWizardState('QUESTIONS');
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Failed to load questions.';
      toast.error(message);
    }
  }, [id, selectedDealTypeIds, checkTemplateVarsAndContinue]);

  // Called when user completes questions step - go straight to evaluation
  const handleQuestionsComplete = useCallback(async () => {
    setWizardState('EVALUATING');
  }, []);

  // ---------------------------------------------------------------------------
  // Template variable handlers
  // ---------------------------------------------------------------------------

  const handleTemplateValueChange = useCallback((key: string, value: string) => {
    setTemplateValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleTemplateVarsComplete = useCallback(async () => {
    if (!id) return;

    try {
      // Save template variables
      await wizardApi.saveTemplateVariables(id, templateValues);

      // Submit
      await handleFinalSubmit();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Failed to save details.';
      toast.error(message);
    }
  }, [id, templateValues, handleFinalSubmit]);

  // ---------------------------------------------------------------------------
  // Error retry
  // ---------------------------------------------------------------------------

  const handleRetry = useCallback(() => {
    setError(null);
    setWizardState('LOADING');
    // Re-trigger the load effect by resetting submission
    setSubmission(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Back navigation between wizard steps
  // ---------------------------------------------------------------------------

  const handleBack = useCallback(() => {
    switch (wizardState) {
      case 'REVIEW':
        setWizardState('UPLOAD');
        break;
      case 'DEAL_TYPE':
        if (submission?.hasDocument && submission?.aiReviewResult) {
          setWizardState('REVIEW');
        }
        // Path B: no back from first step
        break;
      case 'PROPOSAL_PREF':
        setWizardState('DEAL_TYPE');
        break;
      case 'QUESTIONS':
        if (currentPage > 0) {
          // Within questions, go to previous page (handled by QuestionsForm)
          return;
        }
        setWizardState('DEAL_TYPE');
        break;
      case 'TEMPLATE_VARS':
        if (questions.length > 0) {
          setWizardState('QUESTIONS');
        } else {
          setWizardState('DEAL_TYPE');
        }
        break;
      default:
        break;
    }
  }, [wizardState, submission, currentPage, questions.length]);

  /** Whether the current step can go back */
  const canGoBack =
    wizardState === 'REVIEW' ||
    (wizardState === 'DEAL_TYPE' && submission?.hasDocument && !!submission?.aiReviewResult) ||
    wizardState === 'PROPOSAL_PREF' ||
    wizardState === 'TEMPLATE_VARS';
  // Note: QUESTIONS has its own internal back via QuestionsForm; we handle
  // "back from first question page → DEAL_TYPE" separately below.

  // ---------------------------------------------------------------------------
  // Render: Special states
  // ---------------------------------------------------------------------------

  if (wizardState === 'LOADING') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-400">Loading your submission...</p>
        </div>
      </div>
    );
  }

  if (wizardState === 'NOT_FOUND') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 rounded-full bg-grey-800 flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-grey-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">
            Submission Not Found
          </h1>
          <p className="text-slate-400 mb-8">
            We couldn't find the submission you're looking for. It may have
            expired or the link may be incorrect.
          </p>
          <Link
            to="/start"
            className="btn-secondary inline-flex items-center gap-2"
          >
            Start a New Submission
          </Link>
        </div>
      </div>
    );
  }

  if (wizardState === 'ERROR') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-error"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">
            Something Went Wrong
          </h1>
          <p className="text-slate-400 mb-8">
            {error || 'An unexpected error occurred. Please try again.'}
          </p>
          <button onClick={handleRetry} className="btn-secondary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Wizard steps
  // ---------------------------------------------------------------------------

  const hasDocument = submission?.hasDocument ?? false;
  const hasMultipleTypes = selectedDealTypeIds.length > 1;
  const hasTemplateVars = templateVariables.length > 0;

  const stepContext: StepContext = {
    hasDocument,
    hasMultipleTypes,
    hasTemplateVars,
  };

  const currentStep = stepFromState(wizardState, stepContext);
  const title = stepTitle(wizardState);
  const isComplete = wizardState === 'COMPLETE' || wizardState === 'SUBMITTING'
    || wizardState === 'EVALUATING' || wizardState === 'QUALIFIED'
    || wizardState === 'CLARIFYING' || wizardState === 'ACKNOWLEDGED';

  return (
    <WizardLayout currentStep={currentStep} totalSteps={totalSteps(stepContext)} title={title} hideChrome={isComplete}>
      {/* ---- Path A Step 1: Upload ---- */}
      {wizardState === 'UPLOAD' && (
        <div className="space-y-6">
          <MultiFileUpload
            submissionId={id!}
            documents={additionalDocs}
            onDocumentsChange={setAdditionalDocs}
          />

          {/* Continue button - only show when at least 1 doc uploaded */}
          {additionalDocs.length > 0 && (
            <button
              type="button"
              className="w-full rounded-lg bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
              onClick={handleContinueToAnalysis}
            >
              Continue to Analysis
            </button>
          )}
        </div>
      )}

      {/* ---- Path A Step 1b: Analyzing ---- */}
      {wizardState === 'ANALYZING' && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mb-6" />
          <h3 className="text-heading-3 text-white mb-2">
            Analyzing your document{additionalDocs.length > 1 ? 's' : ''}...
          </h3>
          <p className="text-slate-400 max-w-sm">
            Our AI is reviewing{' '}
            {additionalDocs.length > 1
              ? `${additionalDocs.length} documents`
              : (additionalDocs[0]?.fileName || 'your document')
            }{' '}
            to understand your project requirements. This usually takes 15-30 seconds.
          </p>
        </div>
      )}

      {/* ---- Path A Step 2: Extraction Review ---- */}
      {wizardState === 'REVIEW' && (
        <ExtractionReview
          metadata={submission?.aiReviewResult?.extractedMetadata ?? {}}
          documents={additionalDocs}
          onContinue={handleReviewContinue}
          onBack={() => setWizardState('UPLOAD')}
        />
      )}

      {/* ---- Deal Type Selection (both paths) ---- */}
      {wizardState === 'DEAL_TYPE' && (
        <div className="space-y-6">
          <DealTypeSelector
            dealTypes={dealTypes}
            selectedIds={selectedDealTypeIds}
            onToggle={handleDealTypeToggle}
            suggestedIds={suggestedDealTypeIds}
            isLoading={dealTypesLoading}
          />
          <div className="flex gap-3">
            {canGoBack && (
              <button
                type="button"
                onClick={handleBack}
                className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
              >
                Back
              </button>
            )}
            <button
              type="button"
              disabled={selectedDealTypeIds.length === 0}
              onClick={handleDealTypeContinue}
              className="flex-1 rounded-lg bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* ---- Proposal Preference (when 2+ types selected) ---- */}
      {wizardState === 'PROPOSAL_PREF' && (
        <ProposalPreferenceStep
          selectedCount={selectedDealTypeIds.length}
          preference={proposalPreference}
          onSelect={handleProposalPreferenceSelect}
        />
      )}

      {/* ---- Questions ---- */}
      {wizardState === 'QUESTIONS' && (
        <QuestionsForm
          questions={questions}
          answers={answers}
          currentPage={currentPage}
          onAnswer={handleAnswerChange}
          onNextPage={handleNextPage}
          onPrevPage={currentPage === 0 ? () => setWizardState('DEAL_TYPE') : handlePreviousPage}
          onSubmit={handleQuestionsComplete}
          showBack
        />
      )}

      {/* ---- Template Variables ---- */}
      {wizardState === 'TEMPLATE_VARS' && (
        <div className="space-y-6">
          <TemplateVariablesStep
            variables={templateVariables}
            values={templateValues}
            onChange={handleTemplateValueChange}
            extractedMetadata={submission?.aiReviewResult?.extractedMetadata}
          />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleTemplateVarsComplete}
              className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={handleTemplateVarsComplete}
              className="flex-1 rounded-lg bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* ---- AI SDR Evaluating ---- */}
      {wizardState === 'EVALUATING' && (
        <EvaluatingStep message="Reviewing your project..." />
      )}

      {/* ---- Qualified - Show booking CTA ---- */}
      {wizardState === 'QUALIFIED' && (
        <QualifiedStep
          bookingMessage={aiSdrResult?.bookingMessage}
          bookingUrl={bookingUrl}
          onBookCall={handleBookCall}
        />
      )}

      {/* ---- Clarifying - AI generated questions ---- */}
      {wizardState === 'CLARIFYING' && (
        <ClarifyingStep
          questions={clarifyingQuestions}
          onSubmit={handleClarifyingSubmit}
        />
      )}

      {/* ---- Acknowledged - Thanks, we'll review ---- */}
      {wizardState === 'ACKNOWLEDGED' && (
        <AcknowledgedStep message={aiSdrResult?.acknowledgmentMessage} />
      )}

      {/* ---- Submitting (legacy) ---- */}
      {wizardState === 'SUBMITTING' && (
        <ConfirmationStep result={null} isLoading={true} />
      )}

      {/* ---- Complete (legacy) ---- */}
      {wizardState === 'COMPLETE' && (
        <ConfirmationStep result={result} isLoading={false} />
      )}
    </WizardLayout>
  );
}
